import type { OiSummary } from '@/lib/api/types';
import { queryClient } from '@/lib/query';
import { useDronesStore } from '@/lib/store/drones';
import { useOiStore } from '@/lib/store/oi';
import { useUiStore } from '@/lib/store/ui';
import { useWaypointProgressStore } from '@/lib/store/waypointProgress';
import {
  type WaypointProgressPoint,
  buildWaypointProgress,
  estimateFinalWaypoint,
  formatAgo,
  formatCountdown,
} from '@/lib/waypoints/progress';
import type { Feature, LineString, Point } from 'geojson';
/**
 * Waypoint route overlay (ENG-40) — dashed planned path + status-coloured
 * waypoint nodes for the SELECTED drone's OI, plus a solid overlay of the
 * ground actually covered so far, plus a persistent on-map T+ countdown
 * label on the current target node (mockup parity — not hover-only) and a
 * second one on the final waypoint carrying the client-side estimate from
 * `estimateFinalWaypoint` while an earlier waypoint is the target.
 * Mirrors `layers/oi.ts`'s ensure/sync/subscribe pattern.
 *
 * The ticket's "Nice to Have" direction-chevron variant (a solid chevron
 * chain replacing this dashed line, per the ticket's reference image) was
 * tried and reverted on the user's call — it read badly on the actual map.
 * Don't re-add it without checking with the user first.
 *
 * Visibility: only when a drone is selected AND its live OI envelope
 * carries a non-empty `waypoints` route (mockup/ticket: "should not show
 * from the main screen with no drone selected, otherwise screen will be
 * too cluttered"). Empty/no selection ⇒ empty FeatureCollection, same
 * technique `oi.ts` uses for its own context gating.
 *
 * "Selected" has two independent sources depending on context: the Drones
 * tab drives `useDronesStore.selectedId` directly, but the OI's tab only
 * ever sets `useOiStore.selectedId` (its own, separate store — see
 * `OIList.tsx`/`OIDetail.tsx`) and never touches `useDronesStore` at all.
 * `selectedDroneRoute()` below bridges that in OI context by resolving the
 * selected OI's `aircraft` field (a drone_id) back to a drone, instead of
 * having OI selection reach into `useDronesStore` itself — that would also
 * trigger `MapCanvas`'s selected-drone camera-pan effect fighting with the
 * OI layer's own polygon-fit-bounds behaviour.
 *
 * Per-waypoint status/ETA comes from `lib/waypoints/progress.ts`, fed by
 * `useWaypointProgressStore` (ENG-86 `waypoint_pred`/`waypoint_approached`
 * alerts). See that module's header for exactly what is and isn't backed
 * by confirmed BE data. "missed" (ENG-40 DoD) is BE's own verdict, read from
 * `reachedWaypoints` (ENG-211): red node. Waypoints BE never examined (tracking
 * began mid-route) are drawn pale as "not tracked". Against a BE that sends no
 * verdicts, the older index-based inference still applies (see progress.ts).
 */
import mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';

const PATH_SRC = 'src-waypoint-path';
const ACTUAL_SRC = 'src-waypoint-actual';
const NODES_SRC = 'src-waypoint-nodes';
const PATH_LAYER = 'lyr-waypoint-path';
const ACTUAL_LAYER = 'lyr-waypoint-actual';
const NODE_LAYER = 'lyr-waypoint-nodes';

// 'done' intentionally matches the solid actual-path line's colour below —
// a flown waypoint should read as part of the same "now solid/dark" segment
// the mockup describes, not a slightly-different shade of the still-planned
// gray. 'upcoming'/'unknown' stay light so 'done' reads as clearly darker.
// 'missed' (BE's verdict, ENG-211 — see progress.ts's module header) reuses
// the same red the DAA/conflict/degraded markers already use elsewhere on this
// map (see lib/map/layers/surveillance.ts), not an invented colour.
// 'untracked' (never examined, not missed) is paler than 'upcoming' so it reads
// as "no information" rather than "still to come".
const NODE_COLOR = [
  'match',
  ['get', 'status'],
  'done',
  '#18181b',
  'next',
  '#22a050',
  'missed',
  '#ef4444',
  'upcoming',
  '#9ca3af',
  'untracked',
  '#d1d5db',
  /* unknown */ '#9ca3af',
] as const;

export function ensureWaypointsLayer(map: MapboxMap) {
  if (!map.getSource(PATH_SRC)) {
    map.addSource(PATH_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addSource(ACTUAL_SRC, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addSource(NODES_SRC, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(PATH_LAYER)) {
    map.addLayer({
      id: PATH_LAYER,
      type: 'line',
      source: PATH_SRC,
      paint: {
        'line-color': '#6b7280',
        'line-width': 2,
        'line-dasharray': [2, 2],
      },
    });
  }
  if (!map.getLayer(ACTUAL_LAYER)) {
    // Drawn on top of the dashed planned path — solidifies the segment the
    // drone has actually flown (mockup: dashed line "darkens and becomes
    // solid" behind the drone).
    map.addLayer({
      id: ACTUAL_LAYER,
      type: 'line',
      source: ACTUAL_SRC,
      paint: {
        'line-color': '#18181b',
        'line-width': 3,
      },
    });
  }
  if (!map.getLayer(NODE_LAYER)) {
    map.addLayer({
      id: NODE_LAYER,
      type: 'circle',
      source: NODES_SRC,
      paint: {
        'circle-radius': ['case', ['get', 'final'], 7, 5],
        'circle-color': NODE_COLOR as never,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  // The persistent T+/ETA label on the current target node ("just some
  // text, as in the mockup" — ENG-40 DoD, not hover-only) used to be a
  // `symbol` layer here, sourced from NODES_SRC. Removed 2026-09-25: it
  // needed an offset from the node to clear the drone's own
  // `mapboxgl.Marker` (name tag + icon + status badge) — a DOM overlay
  // Mapbox always paints above canvas layers, no layer-order fix possible.
  // A small offset (~1.3em) sat inside that footprint and got covered
  // outright whenever the drone was close to its target — invisible
  // exactly when arriving at (in particular) the final waypoint. A wider
  // offset (~5.5em) cleared the marker but traded that for a worse bug: at
  // low zoom, waypoints sit only tens of screen-px apart, so a fixed
  // ~60px offset visibly detached the label from its own node. Replaced
  // with a DOM marker instead (`subscribeWaypointsLayer`'s `etaMarker`,
  // built by `buildEtaMarkerHtml`) — a small, zoom-stable offset that
  // ALWAYS wins the z-order fight via `z-index`, regardless of proximity,
  // so neither bug is possible: max drone marker z-index is 20
  // (`droneZIndex`), this uses 30.
}

/** "15:16 (5 minutes ago)" — the ticket's hover format. */
function clockAgo(iso: string): string {
  const clock = new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${clock} (${formatAgo(iso)})`;
}

function tooltipFor(p: WaypointProgressPoint, finalEtaSeconds: number | null): string {
  if (p.status === 'next') return formatCountdown(p.etaSeconds) ?? '—';
  if (finalEtaSeconds != null) return formatCountdown(finalEtaSeconds) ?? '—';
  if (p.status === 'done') {
    // Passed, BE's report (time + verdict) is a few seconds behind the target
    // moving on — say so instead of a bare dash.
    if (p.pending) return 'Reached · time pending';
    if (!p.passedAt) return '—'; // passed before we started observing
    return clockAgo(p.passedAt);
  }
  if (p.status === 'missed') {
    const off = p.closestApproachM != null ? ` (${Math.round(p.closestApproachM)} m off)` : '';
    // A missed waypoint was still reached at a real time; show it like a done one.
    return `Missed${off}${p.passedAt ? ` · ${clockAgo(p.passedAt)}` : ''}`;
  }
  if (p.status === 'untracked') return 'Not tracked';
  // upcoming / unknown — BE only predicts the immediate next waypoint; the
  // final one gets the client-side estimate above instead.
  return '—';
}

/** Max drone marker z-index (`droneIcon.ts`'s `droneZIndex`, hovered
 *  tier) is 20 — this stays comfortably above every tier so the ETA
 *  label is never covered, regardless of how close the drone is to its
 *  target. See `ensureWaypointsLayer`'s note on why this is a DOM marker
 *  (small, zoom-stable offset) rather than a canvas symbol layer (which
 *  needed a large offset to clear the drone marker, and that offset then
 *  visibly detached the label from its node at low zoom). */
const ETA_MARKER_Z_INDEX = 30;

function buildEtaMarkerHtml(tooltip: string): string {
  return (
    '<div style="position:absolute;left:50%;bottom:100%;transform:translateX(-50%);' +
    'margin-bottom:8px;background:#ffffff;color:#1f2937;font-family:Inter,sans-serif;' +
    'font-weight:700;font-size:11px;padding:2px 6px;border-radius:3px;white-space:nowrap;' +
    `border:1px solid #d1d5db;box-shadow:0 1px 3px rgba(0,0,0,.2);pointer-events:none">${tooltip}</div>`
  );
}

interface EtaLabel {
  marker: mapboxgl.Marker;
  el: HTMLElement;
}

/** Create, move, or remove one persistent T+ label so it tracks `node`.
 *  Kept in place and updated via innerHTML/setLngLat rather than removed
 *  and recreated each tick, mirroring `predProjection.ts`. */
function placeEtaLabel(
  map: MapboxMap,
  label: EtaLabel | null,
  node: Feature<Point> | undefined,
): EtaLabel | null {
  if (!node) {
    label?.marker.remove();
    return null;
  }
  const lngLat = node.geometry.coordinates as [number, number];
  const html = buildEtaMarkerHtml(String(node.properties?.tooltip ?? '—'));
  if (label) {
    label.marker.setLngLat(lngLat);
    label.el.innerHTML = html;
    return label;
  }
  const el = document.createElement('div');
  el.style.cssText = `pointer-events:none;z-index:${ETA_MARKER_Z_INDEX}`;
  el.innerHTML = html;
  const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
    .setLngLat(lngLat)
    .addTo(map);
  return { marker, el };
}

/** The OI's tab never sets `useDronesStore.selectedId` — resolve the
 *  selected OI's `aircraft` field (a drone_id) instead. REST-first (the
 *  OI list query cache, same source `layers/oi.ts` reads), falling back to
 *  scanning live per-drone OI envelopes for a matching `oi_id` when the
 *  REST cache hasn't loaded yet — mirrors `layers/oi.ts`'s own REST/live
 *  fallback pattern rather than inventing a new one. */
function resolveDroneIdForSelectedOi(): string | null {
  const oiId = useOiStore.getState().selectedId;
  if (!oiId) return null;
  const oiList = queryClient.getQueryData<OiSummary[]>(['oi', 'list']) ?? [];
  const fromRest = oiList.find((o) => o.oi_id === oiId)?.aircraft;
  if (fromRest) return fromRest;
  for (const d of Object.values(useDronesStore.getState().byId)) {
    if (d.oi?.oi_id === oiId) return d.telemetry.drone_id;
  }
  return null;
}

function selectedDroneRoute(isOiContext: boolean): {
  droneId: string;
  waypoints: [number, number][];
  currentPos: [number, number];
} | null {
  const droneId = isOiContext
    ? resolveDroneIdForSelectedOi()
    : useDronesStore.getState().selectedId;
  if (!droneId) return null;
  const d = useDronesStore.getState().byId[droneId];
  const waypoints = d?.oi?.waypoints;
  if (!d || !waypoints || waypoints.length === 0) return null;
  return { droneId, waypoints, currentPos: [d.telemetry.lng, d.telemetry.lat] };
}

/** Returns the node features it just published, so callers (a currently-open
 *  hover popup) can refresh their content from the same computation instead
 *  of going stale between hover-enter and the next store update. */
export function syncWaypointsLayer(map: MapboxMap): Feature<Point>[] {
  const pathSrc = map.getSource(PATH_SRC) as GeoJSONSource | undefined;
  const actualSrc = map.getSource(ACTUAL_SRC) as GeoJSONSource | undefined;
  const nodesSrc = map.getSource(NODES_SRC) as GeoJSONSource | undefined;
  if (!pathSrc || !actualSrc || !nodesSrc) return [];

  const ctx = useUiStore.getState().activeContext;
  const route = ctx === 'drones' || ctx === 'oi' ? selectedDroneRoute(ctx === 'oi') : null;
  if (!route) {
    pathSrc.setData({ type: 'FeatureCollection', features: [] });
    actualSrc.setData({ type: 'FeatureCollection', features: [] });
    nodesSrc.setData({ type: 'FeatureCollection', features: [] });
    return [];
  }

  const pathFeature: Feature<LineString> = {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: route.waypoints },
    properties: {},
  };
  pathSrc.setData({ type: 'FeatureCollection', features: [pathFeature] });

  const pred = useWaypointProgressStore.getState().latestPredByDrone[route.droneId] ?? null;
  const passedAt = useWaypointProgressStore.getState().passedAtByDrone[route.droneId] ?? {};
  const missedDetectionEnabled = Boolean(
    useWaypointProgressStore.getState().structuredApproachedByDrone[route.droneId],
  );
  const wp = useWaypointProgressStore.getState();
  const progress = buildWaypointProgress(route.waypoints, pred, passedAt, missedDetectionEnabled, {
    reached: wp.reachedByDrone[route.droneId],
    untrackedBelow: wp.untrackedBelowByDrone[route.droneId] ?? null,
    verdicts: wp.verdictsByDrone[route.droneId] ?? false,
  });
  const lastIdx = progress.length - 1;
  const finalEstimate = pred ? estimateFinalWaypoint(route.waypoints, pred, Date.now()) : null;

  // "Ground actually covered" — the contiguous prefix of waypoints reached
  // (stops at the first gap, e.g. a missed one) plus the drone's live
  // position for the in-progress leg toward the current target. Sourced
  // from route geometry + status, NOT `drone.trail` (a bounded ~60s/240pt
  // recent-only ring buffer — see its own doc comment): using that here
  // made the solid line slide forward with the drone like a snake instead
  // of staying pinned behind completed waypoints, since old trail points
  // age out from under it (reported bug, 2026-09-25).
  // A waypoint BE confirmed reached counts as flown even when it was "missed"
  // (the drone did fly past it); one merely inferred missed does not. The line
  // starts at the first tracked waypoint — nothing was flown-and-seen before it.
  const startIdx = Math.max(
    0,
    progress.findIndex((p) => p.status !== 'untracked'),
  );
  let doneThrough = startIdx - 1;
  for (const p of progress.slice(startIdx)) {
    const flown = p.status === 'done' || (p.status === 'missed' && p.reached);
    if (flown && p.index === doneThrough + 1) doneThrough = p.index;
    else break;
  }
  const actualFeatures: Feature<LineString>[] =
    doneThrough >= startIdx
      ? [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [...route.waypoints.slice(startIdx, doneThrough + 1), route.currentPos],
            },
            properties: {},
          },
        ]
      : [];
  actualSrc.setData({ type: 'FeatureCollection', features: actualFeatures });

  const nodeFeatures: Feature<Point>[] = progress.map((p) => {
    const isFinal = p.index === lastIdx;
    const showFinalEstimate = isFinal && p.status === 'upcoming' && finalEstimate != null;
    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      properties: {
        index: p.index,
        status: p.status,
        final: isFinal,
        label: `Waypoint ${p.index + 1}${isFinal ? ' (Final)' : ''}`,
        tooltip: tooltipFor(p, showFinalEstimate ? finalEstimate.etaSeconds : null),
        finalEstimate: showFinalEstimate,
      },
    };
  });
  nodesSrc.setData({ type: 'FeatureCollection', features: nodeFeatures });
  return nodeFeatures;
}

function buildPopupHtml(props: Record<string, unknown>): string {
  const label = String(props.label ?? '');
  const tooltip = String(props.tooltip ?? '—');
  return (
    `<div style="font-family:Inter,sans-serif;min-width:120px">` +
    `<div style="font-weight:700;font-size:0.769em;margin-bottom:2px">${label}</div>` +
    `<div style="font-size:0.846em;font-family:IBM Plex Mono,monospace">${tooltip}</div></div>`
  );
}

export function subscribeWaypointsLayer(map: MapboxMap): () => void {
  ensureWaypointsLayer(map);
  syncWaypointsLayer(map);

  const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });
  // The node currently hovered, kept fresh below — without this, an open
  // popup showed whatever the T+countdown/time-ago text was AT mouseenter
  // and never updated again, since Mapbox `mouseenter` doesn't re-fire while
  // the pointer stays put over the same feature.
  let hoveredIndex: number | null = null;
  // The persistent T+ DOM labels — see `buildEtaMarkerHtml`'s doc comment
  // for why these are markers, not a canvas symbol layer. At most one each:
  // the current 'next' node, and the final node while it carries the
  // client-side estimate (never both on the same node).
  let nextLabel: EtaLabel | null = null;
  let finalLabel: EtaLabel | null = null;

  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const nodes = syncWaypointsLayer(map);

      nextLabel = placeEtaLabel(
        map,
        nextLabel,
        nodes.find((n) => n.properties?.status === 'next'),
      );
      finalLabel = placeEtaLabel(
        map,
        finalLabel,
        nodes.find((n) => n.properties?.finalEstimate === true),
      );

      if (hoveredIndex == null) return;
      const hovered = nodes.find((n) => n.properties?.index === hoveredIndex);
      if (!hovered) {
        // Route changed under the open popup (e.g. selection cleared).
        hoveredIndex = null;
        popup.remove();
        return;
      }
      popup.setHTML(buildPopupHtml((hovered.properties ?? {}) as Record<string, unknown>));
    });
  };

  const unsubD = useDronesStore.subscribe((s) => s.selectedId, schedule);
  const unsubDrones = useDronesStore.subscribe((s) => s.byId, schedule);
  const unsubUi = useUiStore.subscribe((s) => s.activeContext, schedule);
  // OI's tab selection — see resolveDroneIdForSelectedOi()'s doc comment.
  const unsubOi = useOiStore.subscribe((s) => s.selectedId, schedule);
  // Re-sync once the OI list REST query lands, in case a route was selected
  // via the OI's tab before `resolveDroneIdForSelectedOi()` had REST data to
  // resolve `aircraft` from (mirrors `layers/oi.ts`'s own query-cache sync).
  const qUnsub = queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === 'oi') schedule();
  });
  const unsubProgress = useWaypointProgressStore.subscribe(schedule);
  // Keep the "T+mm:ss" countdown / "N minutes ago" tooltip text fresh
  // between alert updates — the underlying prediction doesn't change every
  // second, but the elapsed-time labels (and an open popup, above) should
  // still tick.
  const tickId = window.setInterval(schedule, 1000);

  const onMouseEnter = (
    e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] },
  ) => {
    map.getCanvas().style.cursor = 'pointer';
    const f = e.features?.[0];
    if (!f || f.geometry.type !== 'Point') return;
    hoveredIndex = (f.properties as { index?: number } | null)?.index ?? null;
    popup
      .setLngLat(f.geometry.coordinates as [number, number])
      .setHTML(buildPopupHtml((f.properties ?? {}) as Record<string, unknown>))
      .addTo(map);
  };
  const onMouseLeave = () => {
    map.getCanvas().style.cursor = '';
    hoveredIndex = null;
    popup.remove();
  };
  map.on('mouseenter', NODE_LAYER, onMouseEnter);
  map.on('mouseleave', NODE_LAYER, onMouseLeave);

  return () => {
    unsubD();
    unsubDrones();
    unsubUi();
    unsubOi();
    qUnsub();
    unsubProgress();
    window.clearInterval(tickId);
    map.off('mouseenter', NODE_LAYER, onMouseEnter);
    map.off('mouseleave', NODE_LAYER, onMouseLeave);
    popup.remove();
    nextLabel?.marker.remove();
    finalLabel?.marker.remove();
  };
}
